import React from 'react'
import { Box, useMediaQuery } from '@mui/material'
import type { ReactNode } from 'react'

import { BEIGE_200, BEIGE_700, MOBILE_VIEW_WIDTH_QUERY, WHITE } from 'constants/styling/theme'

import { EmbedCoBranding } from 'components/common/Embed/EmbedCoBranding.component'

import { CartSidebar, MobileBottomBar } from './cart'
import { PfProgressStepper } from './PfProgressStepper.component'
import { PfTopBar } from './PfTopBar.component'
import { usePfStepperVariant } from './usePfStepperVariant'
import { usePfEmbed } from '../routing/usePfEmbed'
import { useRightRailRefs } from 'utils/hooks/rightRailRegistry'
import {
  PF_MOBILE_BOTTOM_BAR_HEIGHT,
  PF_STEPPER_HEIGHT,
  PF_STEPPER_HEIGHT_MOBILE,
  PF_STICKY_TOP_OFFSET,
  PF_STICKY_TOP_OFFSET_EMBED,
  PF_STICKY_TOP_OFFSET_MOBILE,
  PF_STICKY_TOP_OFFSET_TOPBAR,
  PF_STICKY_TOP_VAR,
  PF_TOPBAR_HEIGHT,
  PF_TOPBAR_HEIGHT_MOBILE
} from './stickyOffsets'

/**
 * Shared step-level layout shell for PF revamp pages.
 *
 * Provides:
 *   - Beige page background
 *   - Sticky top zone: top bar (PfTopBar) + progress stepper (PfProgressStepper)
 *   - Max-width container with consistent horizontal padding
 *   - Two-column grid: main content (left) + cart sidebar (right, desktop only)
 *   - Mobile cart access via the fixed MobileBottomBar (Total + "See summary")
 *
 * Phase C-B: the per-step Back/Next sticky footer was removed. Step navigation
 * now lives inside `CartSidebar`'s pinned bottom bar (see `StepNavSection`),
 * so the shell no longer renders a `footer` prop.
 *
 * F6: top bar is sticky (position: sticky, top: 0, zIndex: 3).
 * F2: CartSidebar is always present on desktop across all step pages.
 */
/** Fixed width of the desktop cart column, pinned to the page's right edge. */
export const PF_CART_WIDTH = '35rem'

/**
 * Below this width the cart is a drawer behind the bottom bar rather than a
 * rail. The same query the rest of the flow calls "mobile": the rail used to
 * hide at MUI's `md` (900) while the bottom bar only appeared below 800, so
 * 801-899px got neither the cart nor a way to reach it.
 */
export const PF_CART_HIDDEN_QUERY = MOBILE_VIEW_WIDTH_QUERY

/** Wide enough for the embed's co-branding beside the band stepper; German step names fit from ~860px. */
export const PF_EMBED_COBRANDING_QUERY = '(min-width:900px)'

/**
 * Below this width the progress stepper goes back to its own band row.
 *
 * The inline cluster CANNOT shrink — every step is `flexShrink: 0` with a
 * `nowrap` label, and the inline connectors are `flex: 0 0 auto` — so once it
 * stops fitting it does not compress, it collides with the title on one side and
 * the save indicator on the other. `!isMobile` was not a sufficient guard: the
 * mobile query is 800px and the cluster is far wider than the room left there.
 *
 * Measured in Chromium against the real top bar, German (the longest of the six
 * locales) at the full five steps: the cluster is **739px**, the centre slot adds
 * 48px of padding and the title takes 155px, so it collides at 900px and is clean
 * from 1000px — with a save indicator that was rendering empty. A populated one
 * costs another ~110px, which puts the true edge near 1100. 1200 is that edge plus
 * slack, and a round breakpoint.
 */
export const PF_STEPPER_INLINE_QUERY = '(min-width:1200px)'

/** The reading column never grows past this; it centres in whatever is left of
 *  the viewport once the cart has taken its 35rem. */
export const PF_CONTENT_MAX_WIDTH = '100rem'

/** The header bands run the page's full width — only a gutter, no cap. */
const SHELL_BAND_SX = { px: 2, display: 'flex', alignItems: 'center' } as const

/**
 * Page gutter of the reading column, per surface.
 *
 * The standalone flow keeps its responsive map. The EMBED runs the narrow value at every width,
 * because the Propstack panel has already spent ~289px of the window before the iframe starts — so
 * the flow never has the width the wide gutter was designed for, and at the widths the panel hands
 * a laptop the reading column is the only thing that can give. The reclaimed width goes to it: the
 * cart rail is fixed and the gutter is all there is between them.
 *
 * Legacy reached the same conclusion for its own (much deeper) chrome in `purchaseFlowLayout.ts`;
 * this is the v2 equivalent, not a port — v2 has one gutter and one fixed rail where legacy stacked
 * a page container, a step column, a StepWrapper and a filter column.
 */
export const PF_CONTENT_GUTTER = { xs: 1.6, md: 6.4 }
export const PF_CONTENT_GUTTER_EMBED = PF_CONTENT_GUTTER.xs

/** Bands carry the same decision, so the header edge and the content edge stay on one line. */
export const PF_BAND_GUTTER = 2
export const PF_BAND_GUTTER_EMBED = PF_CONTENT_GUTTER.xs

/** Horizontal chrome of the shell for the given surface. */
export const shellChrome = (isEmbed: boolean) => ({
  contentGutter: isEmbed ? PF_CONTENT_GUTTER_EMBED : PF_CONTENT_GUTTER,
  bandGutter: isEmbed ? PF_BAND_GUTTER_EMBED : PF_BAND_GUTTER,
})

/**
 * One horizontal band of the shell: a fluid reading column capped at
 * [PF_CONTENT_MAX_WIDTH] and centred, plus a fixed right rail the width of the
 * cart.
 */
const ShellRow: React.FC<{
  children: ReactNode
  rail?: ReactNode
  withRail: boolean
  gutter: number | Record<string, number>
}> = ({ children, rail, withRail, gutter }) => {
  const { contentRef, railRef } = useRightRailRefs<HTMLDivElement, HTMLDivElement>()

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: gutter,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {/* pb so a step's last control — the configuration step's Save, say —
            is not flush with the bottom of the page. The rail is excluded: it
            is sticky and sets its own height against the viewport. */}
        <Box ref={contentRef} sx={{ width: '100%', maxWidth: PF_CONTENT_MAX_WIDTH, minWidth: 0, pb: 2.4 }}>
          {children}
        </Box>
      </Box>

      {withRail &&
        <Box ref={railRef} sx={{ width: PF_CART_WIDTH, flexShrink: 0 }}>
          {rail}
        </Box>
      }

    </Box>
  )
}

interface Props {
  /** Shown left of the topbar's autosave indicator. Defaults to 'Your order'. */
  topBarTitle?: string
  /**
   * Optional subtitle shown below the topbar title in smaller text.
   * Used on the Summary step ("Review your order before confirmation").
   */
  topBarSubtitle?: string
  /** Hide the cart sidebar — used only by the property-first entry page. */
  hideCart?: boolean
  children: ReactNode
}

export const StepShell: React.FC<Props> = ({ topBarTitle, topBarSubtitle, hideCart = false, children }) => {
  const hasCartRail = !useMediaQuery(PF_CART_HIDDEN_QUERY)
  const { isEmbed } = usePfEmbed()
  const fitsCoBranding = useMediaQuery(PF_EMBED_COBRANDING_QUERY)
  const { contentGutter, bandGutter } = shellChrome(isEmbed)
  const showsBottomBar = !hideCart && !hasCartRail

  // 'topbar' hosts the stepper inline in the top bar and drops the band row —
  // the default since the 2026-09-18 review, with `?pfStepper=band` still
  // reaching the other layout. Narrow viewports keep the band whatever the
  // variant says, because the inline cluster cannot shrink to fit one.
  const stepperVariant = usePfStepperVariant()
  const fitsInlineStepper = useMediaQuery(PF_STEPPER_INLINE_QUERY)
  // Inside the embed there is no top bar at all, as in legacy — Propstack's page
  // is the chrome — so the stepper keeps its own band there.
  const inlineStepper = !isEmbed && fitsInlineStepper && stepperVariant === 'topbar'

  // Sticky-zone height of the active variant. Published as the `--pf-sticky-top`
  // CSS var so every sticky child below (tab strip, cart sidebar, info rail)
  // pins at the right offset without re-resolving the variant.
  // On mobile the stepper is only its segment bar, so its row is shorter.
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const stepperHeight = isMobile ? PF_STEPPER_HEIGHT_MOBILE : PF_STEPPER_HEIGHT
  const stickyTopOffset = isEmbed
    ? (isMobile ? PF_STEPPER_HEIGHT_MOBILE : PF_STICKY_TOP_OFFSET_EMBED)
    : inlineStepper ? PF_STICKY_TOP_OFFSET_TOPBAR : isMobile ? PF_STICKY_TOP_OFFSET_MOBILE : PF_STICKY_TOP_OFFSET

  return (
    <Box
      sx={{
        [PF_STICKY_TOP_VAR]: stickyTopOffset,
        backgroundColor: BEIGE_200,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >

      {/* Sticky top zone — topbar row (PF_TOPBAR_HEIGHT) + progress stepper row
          (PF_STEPPER_HEIGHT) stacked, total height pinned to PF_STICKY_TOP_OFFSET
          so the catalogue tab strip and cart sidebar (both sticky at that
          offset) sit FLUSH against its bottom. Sub-pixel gaps let scrolled
          card content bleed through, so we lock the wrapper + each row's height
          instead of relying on padding math.
          F19: zIndex bumped to 10 so the BeforeAfterSlider drag handle
          (zIndex 3 inside its own card) cannot escape upward into the
          topbar zone during scroll. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          // White header over a BEIGE_200 page, as the prototype measures.
          backgroundColor: WHITE,
          height: stickyTopOffset,
          // Runs the full viewport width — this wrapper has no max-width or
          // gutters — and the cart column's left rule starts flush under it,
          // so the two read as one continuous edge.
          borderBottom: `1px solid ${BEIGE_700}`,
        }}
      >

        {!isEmbed &&
        <Box sx={{ ...SHELL_BAND_SX, px: bandGutter, height: isMobile ? PF_TOPBAR_HEIGHT_MOBILE : PF_TOPBAR_HEIGHT }} data-cy="pf-revamp-topbar-row">
          <PfTopBar
            title={topBarTitle}
            subtitle={topBarSubtitle}
            center={inlineStepper ? <PfProgressStepper placement="inline" /> : undefined}
          />
        </Box>
        }

        {/* Band row — only when the stepper is NOT inlined in the top bar
            (i.e. band variant, or any mobile viewport). Inside the embed it is
            the only persistent row, so it carries the BACKBONE × Propstack
            co-branding legacy shows in its footer. */}
        {!inlineStepper &&
          <Box sx={{ ...SHELL_BAND_SX, px: bandGutter, height: stepperHeight, gap: 3.2 }}>
            {isEmbed && fitsCoBranding &&
              <EmbedCoBranding />
            }
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PfProgressStepper placement="band" />
            </Box>
          </Box>
        }

      </Box>

      {/* Main content area — grows to fill available space.
          F14: pt: 0 so the cart sidebar and any sticky child elements (e.g.
          catalogue tab strip) sit flush against the topbar bottom — they're
          already at their sticky pin position from scrollY=0, so no
          scroll-then-pin movement at the top.
          On mobile the fixed MobileBottomBar overlays the viewport bottom, so
          reserve its height (+ breathing room) as bottom padding or the last
          cards hide behind it. */}
      <Box
        sx={{
          flex: 1,
          pb: showsBottomBar ? `calc(${PF_MOBILE_BOTTOM_BAR_HEIGHT} + 1.6rem)` : 0,
        }}
      >
        <ShellRow
          withRail={!hideCart && hasCartRail}
          rail={<CartSidebar />}
          gutter={contentGutter}
        >
          {children}
        </ShellRow>
      </Box>

      {/* Mobile fixed bottom action bar (Total + "See summary" → cart sheet) */}
      {/* Whenever the rail is not on screen the cart is only reachable here. */}
      {showsBottomBar &&
        <MobileBottomBar />
      }

    </Box>
  )
}
